/**
 * Clip Panel Component (canvas-v2)
 *
 * 2-column scrollable thumbnail grid showing all reference video and audio clips.
 * Always displays connected reference clips from edges, even if removed from timeline.
 * Reference cards are reusable sources: click selects the reference,
 * drag/drop places a new instance on a compatible track.
 */

import { memo, useMemo } from 'react'
import { Film, Plus } from 'lucide-react'
import { getAssetUrl } from '@ponpon/api-client'
import { useAuthenticatedMediaUrl } from '../../../../hooks/useAuthenticatedMediaUrl'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { StaticWaveform } from './StaticWaveform'
import type { TimelineClip, TimelineAudioClip } from './types'

interface ClipPanelProps {
  /** Video clips currently on the timeline */
  clips: TimelineClip[]

  /** Audio clips currently on the timeline */
  audioClips: TimelineAudioClip[]

  /** All connected reference video clips (from edges) */
  referenceVideoClips?: Array<{ id: string; path: string; name: string }>

  /** All connected reference audio clips (from edges) */
  referenceAudioClips?: Array<{ id: string; path: string; name: string; role?: string }>

  /** Set of currently selected clip IDs (multi-selection) */
  selectedClipIds: Set<string>

  /** Callback when a clip is selected */
  onSelectClip: (clipId: string, clipType: 'video' | 'audio') => void

  /** Callback to scroll the timeline to a clip */
  onScrollToClip: (clipId: string, clipType: 'video' | 'audio') => void

  /** Callback to add a reference clip to the timeline */
  onAddClipToTimeline?: (clipId: string, clipType: 'video' | 'audio') => void

  /**
   * Opens the reference-selector modal (RFC-0651 #6). When provided, the
   * ClipPanel renders a full-width "+ Add References" FOOTER button at the
   * bottom of the panel (RFC-0631 Issue 8 — was a header button) that calls
   * this. Plumbed through TimelineEditorModal from the VideoEditorNode
   * toolbar.
   */
  onOpenReferenceSelector?: () => void
}

export const ClipPanel = memo(function ClipPanel({
  clips,
  audioClips,
  referenceVideoClips,
  referenceAudioClips,
  selectedClipIds,
  onSelectClip,
  onScrollToClip,
  onOpenReferenceSelector,
}: ClipPanelProps) {
  // Use reference clips if available, otherwise fall back to timeline clips
  const videoRefs = referenceVideoClips
  const audioRefs = referenceAudioClips

  // Fall back to legacy behavior if no reference props provided
  const sortedClips = useMemo(
    () => [...clips].sort((a, b) => a.order - b.order),
    [clips]
  )

  const handleClipClick = (clipId: string, clipType: 'video' | 'audio') => {
    onSelectClip(clipId, clipType)
    onScrollToClip(clipId, clipType)
  }

  const handleReferenceClick = (clipId: string, clipType: 'video' | 'audio') => {
    onSelectClip(clipId, clipType)
  }

  const hasVideoRefs = videoRefs ? videoRefs.length > 0 : sortedClips.length > 0
  const hasAudioRefs = audioRefs ? audioRefs.length > 0 : audioClips.length > 0

  // RFC-0631 (Issue 8): the "+ Add References" button lives in a sticky
  // FOOTER (full-width, bottom-anchored) instead of the panel HEADER. This
  // matches the canvas-node pattern (`VideoEditorBottomPanel`) and keeps the
  // CTA visible regardless of the scroll position. Both the empty-state and
  // populated branches share this footer.
  const footer = onOpenReferenceSelector ? (
    <div className="mt-auto border-t border-border-subtle p-2">
      <button
        type="button"
        onClick={onOpenReferenceSelector}
        className="flex w-full items-center justify-center gap-1 rounded px-2 py-1.5 text-xs text-text-secondary hover:bg-surface-2 hover:text-text-primary transition-colors border border-border-subtle"
        data-testid="timeline-editor-add-references"
        title="Add reference video / audio nodes"
      >
        <Plus className="w-3 h-3" />
        Add References
      </button>
    </div>
  ) : null

  if (!hasVideoRefs && !hasAudioRefs) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-center flex-1 text-text-muted text-sm p-4">
          No clips connected
        </div>
        {footer}
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 min-h-0 overflow-y-auto p-2">
        {/* Video clips section */}
        {videoRefs ? (
          videoRefs.length > 0 && (
            <>
              <div className="text-text-secondary text-xs font-medium px-1 py-1.5">
                Video ({videoRefs.length})
              </div>
              <div className="grid grid-cols-2 gap-2">
                {videoRefs.map((ref) => {
                  return (
                    <ClipThumbnail
                      key={ref.id}
                      id={ref.id}
                      label={ref.name}
                      sourcePath={ref.path}
                      isSelected={selectedClipIds.has(ref.id)}
                      type="video"
                      isOnTimeline={false}
                      onClick={() => handleReferenceClick(ref.id, 'video')}
                    />
                  )
                })}
              </div>
            </>
          )
        ) : (
          sortedClips.length > 0 && (
            <>
              <div className="text-text-secondary text-xs font-medium px-1 py-1.5">
                Video ({sortedClips.length})
              </div>
              <div className="grid grid-cols-2 gap-2">
                {sortedClips.map((clip) => (
                  <ClipThumbnail
                    key={clip.id}
                    id={clip.id}
                    label={clip.label}
                    sourcePath={clip.sourcePath}
                    isSelected={selectedClipIds.has(clip.id)}
                    type="video"
                    isOnTimeline={true}
                    onClick={() => handleClipClick(clip.id, 'video')}
                  />
                ))}
              </div>
            </>
          )
        )}

        {/* Audio clips section */}
        {audioRefs ? (
          audioRefs.length > 0 && (
            <>
              <div className="text-text-secondary text-xs font-medium px-1 py-1.5 mt-2">
                Audio ({audioRefs.length})
              </div>
              <div className="grid grid-cols-2 gap-2">
                {audioRefs.map((ref) => {
                  return (
                    <ClipThumbnail
                      key={ref.id}
                      id={ref.id}
                      label={ref.name}
                      sourcePath={ref.path}
                      isSelected={selectedClipIds.has(ref.id)}
                      type="audio"
                      isOnTimeline={false}
                      onClick={() => handleReferenceClick(ref.id, 'audio')}
                    />
                  )
                })}
              </div>
            </>
          )
        ) : (
          audioClips.length > 0 && (
            <>
              <div className="text-text-secondary text-xs font-medium px-1 py-1.5 mt-2">
                Audio ({audioClips.length})
              </div>
              <div className="grid grid-cols-2 gap-2">
                {audioClips.map((clip) => (
                  <ClipThumbnail
                    key={clip.id}
                    id={clip.id}
                    label={clip.sourcePath.split('/').pop() || 'Audio'}
                    sourcePath={clip.sourcePath}
                    isSelected={selectedClipIds.has(clip.id)}
                    type="audio"
                    isOnTimeline={true}
                    onClick={() => handleClipClick(clip.id, 'audio')}
                  />
                ))}
              </div>
            </>
          )
        )}
      </div>
      {footer}
    </div>
  )
})

/** Individual clip thumbnail card */
interface ClipThumbnailProps {
  id: string
  label: string
  sourcePath: string
  isSelected: boolean
  type: 'video' | 'audio'
  isOnTimeline?: boolean
  onClick: () => void
}

function ClipThumbnail({
  id,
  label,
  sourcePath,
  isSelected,
  type,
  isOnTimeline = true,
  onClick,
}: ClipThumbnailProps) {
  const projectId = useProjectId()
  const baseUrl = useMemo(() => {
    if (!projectId || !sourcePath) return null
    return getAssetUrl(projectId, sourcePath)
  }, [projectId, sourcePath])
  const mediaUrl = useAuthenticatedMediaUrl(baseUrl)

  return (
    <button
      className={`
        min-w-0 rounded-lg border border-border-subtle bg-surface-1 overflow-hidden text-left transition-colors
        hover:border-input-border-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30
        ${isSelected
          ? 'ring-1 ring-primary/60'
          : ''
        }
      `}
      onClick={onClick}
      draggable
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'copy'
        event.dataTransfer.setData(
          'application/x-ponpon-timeline-reference',
          JSON.stringify({ id, type })
        )
      }}
      title={label}
      data-testid={`timeline-editor-${type}-reference`}
      aria-pressed={isOnTimeline}
    >
      <div className="h-14 bg-input-bg overflow-hidden">
        {type === 'video' ? (
          mediaUrl ? (
            <video
              src={mediaUrl}
              className="h-full w-full object-cover"
              muted
              playsInline
              preload="metadata"
            />
          ) : (
            <div className="flex h-full items-center justify-center">
              <Film className="w-4 h-4 text-text-muted" />
            </div>
          )
        ) : (
          <div className="h-full p-2 text-primary/70">
            <StaticWaveform sourcePath={sourcePath} barCount={22} variant="classic" />
          </div>
        )}
      </div>
      <div className="px-1.5 py-1">
        <span className="block truncate text-xs text-muted-foreground">{label}</span>
      </div>
    </button>
  )
}
